import { InfoTooltip } from '@/components/ui/info-tooltip';

interface ScoreFactor {
  name: string;
  score: number;
  weight: number;
  status?: 'passed' | 'failed' | 'unknown';
  details?: string;
}

interface FactorBarProps {
  factor: ScoreFactor;
}

export function FactorBar({ factor }: FactorBarProps) {
  const status = factor.status ?? 'unknown';
  const isMissingData = status === 'unknown';
  const displayScore = isMissingData ? 'N/A' : factor.score.toString();
  const barWidth = isMissingData ? 0 : factor.score;

  // Match badge faded background colors: hsl(var(--X) / 0.2)
  const barColor = isMissingData
    ? 'hsl(var(--muted-foreground) / 0.15)'
    : factor.score >= 80
      ? 'hsl(var(--success) / 0.5)'
      : factor.score >= 50
        ? 'hsl(var(--warning) / 0.5)'
        : 'hsl(var(--destructive) / 0.5)';

  return (
    <div className="py-2.5">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{factor.name}</span>
          {factor.details && (
            <InfoTooltip text={factor.details} label={`Score factor details: ${factor.details}`} />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-tabular">
            {(factor.weight * 100).toFixed(0)}% weight
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div
          className="relative h-2 flex-1 overflow-hidden"
          style={{ backgroundColor: 'hsl(var(--muted) / 0.15)' }}
        >
          <div
            className="absolute inset-y-0 left-0 transition-all duration-300"
            style={{
              width: `${barWidth}%`,
              backgroundColor: barColor,
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 100%, 0 100%)',
            }}
          />
        </div>
        <span className="w-8 text-right text-xs font-medium font-tabular">{displayScore}</span>
      </div>
    </div>
  );
}

export type { ScoreFactor };
